// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Select, MenuItem, InputBase, Divider, Button, Fade, CircularProgress, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { RootState } from 'Store';
import { AssignCommittedTierThunk, GetCommittedTiersThunk } from 'Features/admin/adminThunk';
import DatePicker from '../datePicker';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

const PERCENT = 100;
const roundToCents = (value: number) => Math.round(value * 100) / 100;

export const CommittedTier = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state: RootState) => state.auth);
  const { org, committedTiers, updatingCommittedTier } = useAppSelector((state: RootState) => state.admin);
  const currentTier = org?.currentCommittedTierInfo;

  const [editMode, setEditMode] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState(currentTier?.tierId || '');
  const [annualCommit, setAnnualCommit] = useState(currentTier?.annualCommit || 0);
  const [dollarPerCredit, setDollarPerCredit] = useState(currentTier?.dollarPerCredit || 0);
  const [discountPercent, setDiscountPercent] = useState(currentTier?.discountPercent || 0);
  const [startDate, setStartDate] = useState(currentTier?.startDate || moment(new Date()).format());
  const [endDate, setEndDate] = useState(currentTier?.endDate || moment(new Date()).format());
  const [openStartDatepicker, setOpenStartDatepicker] = useState(false);
  const [openEndDatepicker, setOpenEndDatepicker] = useState(false);

  useEffect(() => {
    dispatch(GetCommittedTiersThunk());
  }, [dispatch]);

  const resetTier = () => {
    setSelectedTierId(currentTier?.tierId || '');
    setAnnualCommit(currentTier?.annualCommit || 0);
    setDollarPerCredit(currentTier?.dollarPerCredit || 0);
    setDiscountPercent(currentTier?.discountPercent || 0);
    setStartDate(currentTier?.startDate || moment(new Date()).format());
    setEndDate(currentTier?.endDate || moment(new Date()).format());
  };

  // Default the price per credit and discount to the tier's saved DB values; both stay
  // editable so an admin can capture a negotiated per-org rate before saving.
  const applyTierDefaults = (tierId: string) => {
    const tier = committedTiers.find((t) => t._id === tierId);
    if (tier) {
      setDollarPerCredit(tier.dollarPerCredit || 0);
      setDiscountPercent(tier.discountPercent || 0);
    }
  };

  // Picking a tier resets the rate and discount to that tier's DB defaults.
  const handleSelectTier = (tierId: string) => {
    setSelectedTierId(tierId);
    applyTierDefaults(tierId);
  };

  // Discount and price per credit are two views of the same deal, anchored on the Standard
  // tier's rate: dollarPerCredit = standard × (1 − discount/100). Editing either keeps the
  // other in sync so an admin can enter a negotiated rate from whichever angle they have.
  const standardDollarPerCredit = committedTiers.find((tier) => tier.name === 'Standard')?.dollarPerCredit || 0;

  const handleDiscountChange = (value: number) => {
    setDiscountPercent(value);
    if (standardDollarPerCredit > 0 && Number.isFinite(value)) {
      setDollarPerCredit(roundToCents(standardDollarPerCredit * (1 - value / PERCENT)));
    }
  };

  const handleDollarPerCreditChange = (value: number) => {
    setDollarPerCredit(value);
    if (standardDollarPerCredit > 0 && Number.isFinite(value)) {
      setDiscountPercent(roundToCents((1 - value / standardDollarPerCredit) * PERCENT));
    }
  };

  // Opening the form defaults every field to the selected tier's saved DB values rather
  // than the org's last negotiated snapshot, so the shown price per credit matches the tier.
  const openEdit = () => {
    const tierId = currentTier?.tierId || '';
    setSelectedTierId(tierId);
    setAnnualCommit(currentTier?.annualCommit || 0);
    setStartDate(currentTier?.startDate || moment(new Date()).format());
    setEndDate(currentTier?.endDate || moment(new Date()).format());
    applyTierDefaults(tierId);
    setEditMode(true);
  };

  const saveTier = async () => {
    const success = await dispatch(AssignCommittedTierThunk({
      tierId: selectedTierId,
      annualCommit,
      dollarPerCredit,
      discountPercent,
      startDate,
      endDate,
    }));
    setEditMode(false);
    if (!success) {
      resetTier();
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    resetTier();
  };

  return (
    <Box>
      <Typography variant="h4" sx={styles.sectionTitle}>Committed Tier</Typography>
      <Divider sx={styles.divider} />
      {!editMode && currentTier && (
        <Box sx={styles.listContent}>
          <Typography>Tier</Typography>
          <Typography>{currentTier.tierName}</Typography>
          <Typography>Annual Commit</Typography>
          <Typography>{`$${currentTier.annualCommit ?? 0}`}</Typography>
          <Typography>Price per Credit</Typography>
          <Typography>{`$${currentTier.dollarPerCredit}`}</Typography>
          <Typography>Discount</Typography>
          <Typography>{`${currentTier.discountPercent}%`}</Typography>
          <Typography>Start Date</Typography>
          <Typography>{moment(currentTier.startDate).format('LL')}</Typography>
          {currentTier.endDate && (
            <>
              <Typography>End Date</Typography>
              <Typography>{moment(currentTier.endDate).format('LL')}</Typography>
            </>
          )}
        </Box>
      )}
      {!editMode && (
        <Box sx={styles.planButtonGroup}>
          <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={openEdit} color="primary" variant="contained">
            {currentTier ? 'Edit Tier' : 'Assign Tier'}
          </Button>
        </Box>
      )}
      {editMode && (
        <>
          <Box sx={styles.formContent}>
            <Typography>Tier</Typography>
            <Box>
              <Select
                variant="outlined"
                input={<InputBase sx={styles.inputBase} />}
                value={selectedTierId}
                onChange={(e) => handleSelectTier(e.target.value)}
              >
                {committedTiers.map((tier) => (
                  <MenuItem value={tier._id} key={tier._id}>{`${tier.name} (${tier.discountPercent}% off)`}</MenuItem>
                ))}
              </Select>
            </Box>
            <Typography>Annual Commit</Typography>
            <Box sx={styles.textFieldDiv}>
              <TextField
                id="annualCommit"
                variant="standard"
                type="number"
                inputProps={{ min: 0 }}
                onChange={(e) => setAnnualCommit(parseFloat(e.target.value))}
                value={annualCommit?.toString()}
                sx={styles.textField}
              />
            </Box>
            <Typography>Price per Credit</Typography>
            <Box sx={styles.textFieldDiv}>
              <TextField
                id="dollarPerCredit"
                variant="standard"
                type="number"
                inputProps={{ min: 0 }}
                onChange={(e) => handleDollarPerCreditChange(parseFloat(e.target.value))}
                value={dollarPerCredit?.toString()}
                sx={styles.textField}
              />
            </Box>
            <Typography>Discount %</Typography>
            <Box sx={styles.textFieldDiv}>
              <TextField
                id="discountPercent"
                variant="standard"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                onChange={(e) => handleDiscountChange(parseFloat(e.target.value))}
                value={discountPercent?.toString()}
                sx={styles.textField}
              />
            </Box>
            <Typography>Start Date</Typography>
            <Box>
              <DatePicker openDatepicker={openStartDatepicker} setOpenDatepicker={setOpenStartDatepicker} selectedDate={startDate} setSelectedDate={setStartDate} />
            </Box>
            <Typography>End Date</Typography>
            <Box>
              <DatePicker openDatepicker={openEndDatepicker} setOpenDatepicker={setOpenEndDatepicker} selectedDate={endDate} setSelectedDate={setEndDate} />
            </Box>
          </Box>
          <Box sx={styles.buttonGroup}>
            <Button
              sx={styles.planButton}
              disabled={!selectedTierId || !(dollarPerCredit > 0) || !(discountPercent >= 0) || !startDate}
              onClick={saveTier}
              color="primary"
              variant="contained"
            >
              <Fade in={updatingCommittedTier}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!updatingCommittedTier}>
                <span>Save Changes</span>
              </Fade>
            </Button>
            <Button sx={styles.planButton} onClick={cancelEdit} variant="text">Cancel</Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CommittedTier;
